import { ObjectListLayout } from "@/components/layout/object-list";
import { ObjectListColumn } from "@/components/layout/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ActionButton } from "@/components/utils/action-button";
import { display_user } from "@/components/utils/display";
import { WithIcon } from "@/components/utils/with-icon";
import { PaginatedResponse } from "@/types/common";
import { ListPlus, Plus, Trash2 } from "lucide-react";
import { DateTime } from "luxon";
import { Link, useLoaderData } from "react-router-dom";
import { DiabolocomInjectionBatch } from "./types";

export function InjectionBatchList() {
    const batches = useLoaderData() as PaginatedResponse<DiabolocomInjectionBatch>;

    const getStatusVariant = (status: string) => {
        switch (status) {
            case "completed":
                return "green";
            case "failed":
                return "destructive";
            case "processing":
                return "amber";
            default:
                return "secondary";
        }
    };

    const columns: ObjectListColumn<DiabolocomInjectionBatch>[] = [
        {
            name: "ID",
            accessorFn: (instance: DiabolocomInjectionBatch) => instance.id
        },
        {
            name: "Type",
            accessorFn: (instance: DiabolocomInjectionBatch) =>
                instance.case_type === "reset" ? (instance.campaign_id === "675" ? "Upsell vidée" : "Flash vidée") : instance.case_type
        },
        {
            name: "Injection",
            accessorFn: (instance: DiabolocomInjectionBatch) => (instance.case_type === "reset" ? "-" : instance.injection_type)
        },
        {
            name: "Progression",
            accessorFn: (instance: DiabolocomInjectionBatch) => (instance.case_type === "reset" ? "-" : instance.progression)
        },
        {
            name: "Fiches injectées",
            accessorFn: (instance: DiabolocomInjectionBatch) => (instance.case_type === "reset" ? "-" : `${instance.injected_cases_number} / ${instance.cases_count}`)
        },
        {
            name: "Dates",
            accessorFn: (instance: DiabolocomInjectionBatch) =>
                instance.case_type === "reset"
                    ? "-"
                    : `${instance.date_range_from ? DateTime.fromISO(instance.date_range_from).toLocaleString(DateTime.DATE_SHORT) : ""} - ${instance.date_range_to ? DateTime.fromISO(instance.date_range_to).toLocaleString(DateTime.DATE_SHORT) : ""}`
        },
        {
            name: "Priorité",
            accessorFn: (instance: DiabolocomInjectionBatch) => (instance.case_type === "reset" ? "-" : instance.priority)
        },
        {
            name: "Assignation",
            accessorFn: (instance: DiabolocomInjectionBatch) =>
                instance.case_type === "reset"
                    ? "-"
                    : instance.assignment_type === "manual"
                      ? display_user(instance.assigned_agent)
                      : instance.assignment_type == "none"
                        ? "Aucune"
                        : "Propriétaire de la complétion"
        },
        {
            name: "Créé par",
            accessorFn: (instance: DiabolocomInjectionBatch) => display_user(instance.created_by)
        },
        {
            name: "Date de création",
            accessorFn: (instance: DiabolocomInjectionBatch) => DateTime.fromISO(instance.created_at).toLocaleString(DateTime.DATETIME_SHORT)
        },
        {
            name: "Statut",
            accessorFn: (instance: DiabolocomInjectionBatch) =>
                instance.case_type === "reset" ? <Badge variant="destructive">Vidée avec succès</Badge> : <Badge variant={getStatusVariant(instance.status)}>{instance.status}</Badge>
        }
    ];

    return (
        <ObjectListLayout
            page_title="Liste des batchs d'injection"
            paginated_response={batches}
            columns={columns}
            actions={[
                <>
                    <Button key="new-injection" asChild>
                        <Link to="new">
                            <WithIcon icon={Plus}>Nouvelle injection</WithIcon>
                        </Link>
                    </Button>
                    <Button key="new-manual" variant="outline" disabled={true}>
                        <WithIcon icon={ListPlus}>Injection manuelle</WithIcon>
                    </Button>
                    <ActionButton variant="destructive" action="/outils/injection-flash/reset_flash" method="post" successNotification="Campagne flash vidée avec succès">
                        <WithIcon icon={Trash2}>Vider la Flash</WithIcon>
                    </ActionButton>
                    <ActionButton
                        variant="destructive"
                        action="/outils/injection-flash/reset_flash"
                        method="post"
                        payload={{ campaign_id: "675" }}
                        successNotification="Campagne upsell généraliste vidée avec succès"
                    >
                        <WithIcon icon={Trash2}>Vider l'upsell généraliste</WithIcon>
                    </ActionButton>
                </>
            ]}
        />
    );
}
